import { CONFIG, MAX_STAGE_SCORE } from '@h2h/shared';
import { GripVertical, TrendingUp } from '../../ui/icons';
import { cn } from '../../../lib/cn';
import { rungWeightStyle } from './rungStyles';

/**
 * Empty stake ladder — same chrome as the ranked hub card, but ghost rungs so
 * players see the 6×→1× shape before they've called any duels.
 */
export function StakeLadderPlaceholder({
  embedded = false,
  calledCount = 0,
  total = CONFIG.CYCLING.DUELS_PER_STAGE
}: {
  embedded?: boolean;
  calledCount?: number;
  total?: number;
}) {
  const weights = [...CONFIG.CYCLING.CONFIDENCE_WEIGHTS];
  const topWeight = Math.max(...weights, 0);
  const ranked = calledCount >= total;

  return (
    <div
      className={cn(
        !embedded &&
          'overflow-hidden rounded-[26px] border border-black/[0.08] bg-white shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]'
      )}
    >
      <div className="relative px-2.5 pb-1.5 pt-3.5">
        <div
          className="pointer-events-none absolute bottom-[34px] left-[39px] top-[30px] w-0.5 rounded-sm bg-gradient-to-b from-[#D8D8E0] to-[#ECECF2]"
          aria-hidden
        />

        {weights.map((weight, i) => (
          <PlaceholderRung key={i} weight={weight} topWeight={topWeight} />
        ))}
      </div>

      <div className="flex items-center gap-2.5 border-t border-black/[0.06] bg-[#FBFBFD] px-[18px] py-[13px]">
        <TrendingUp width={16} height={16} strokeWidth={2.2} className="shrink-0 text-ink4" />
        <span className="flex-1 text-[12.5px] font-semibold tracking-[-0.01em] text-ink3">
          {ranked ? (
            <>
              All six ranked · up to <span className="font-extrabold text-ink2">{MAX_STAGE_SCORE} pts</span> on the line
            </>
          ) : calledCount > 0 ? (
            <>
              Finish your calls · up to <span className="font-extrabold text-ink2">{MAX_STAGE_SCORE} pts</span> on the line
            </>
          ) : (
            <>
              Call all six to rank · up to <span className="font-extrabold text-ink2">{MAX_STAGE_SCORE} pts</span> on the line
            </>
          )}
        </span>
        <span className="num text-xs font-extrabold text-ink4">
          {calledCount} / {total}
        </span>
      </div>
    </div>
  );
}

function PlaceholderRung({ weight, topWeight }: { weight: number; topWeight: number }) {
  const styles = rungWeightStyle(weight, topWeight);
  const faded = weight < topWeight - 2;
  // Pills must stay fully opaque — transparency bleeds the gradient line through.
  // Reduce visual prominence on lower ranks via text opacity on the label only.
  const pillTextOpacity = weight >= topWeight - 2 ? '' : 'opacity-60';

  return (
    <div
      className={cn(
        'relative flex items-center gap-[11px] border-t border-black/[0.05] px-2.5 py-3 first:border-t-0',
        faded && 'opacity-[0.72]'
      )}
    >
      <div
        className={cn(
          'relative z-[2] grid h-[38px] w-[38px] shrink-0 place-items-center rounded-xl',
          styles.pill
        )}
      >
        <span className={cn('text-base font-extrabold tracking-[-0.03em]', styles.text, pillTextOpacity)}>{weight}×</span>
      </div>

      <div className="h-9 w-9 shrink-0 rounded-full border-2 border-dashed border-black/[0.08] bg-[#F6F6F9]" />

      <div className="min-w-0 flex-1">
        <div className="h-[14px] w-[68%] rounded-[6px] bg-[#EBEBF0]" />
        <div className="mt-[9px] flex items-center gap-1.5">
          <span className="text-[10px] font-extrabold tracking-[0.04em] text-ink4">BEATS</span>
          <div className="h-[15px] w-[15px] shrink-0 rounded-full bg-[#EBEBF0]" />
          <div className="h-[10px] w-[42%] rounded-[4px] bg-[#EBEBF0]" />
        </div>
      </div>

      <GripVertical width={18} height={18} strokeWidth={2} className="shrink-0 text-ink4/35" aria-hidden />
    </div>
  );
}
